import { Allocation, UserRegistrationPeriod } from '../api/generated/data-contracts';

type MissingAllocatedEntry = {
    projectId: string;
    projectName: string;
    allocatedHours: number;
    registeredHours: number;
    missingHours: number;
};

/**
 * Given:
 *  - projects: an array of UserRegistrationPeriod objects (each one has a projectId, a projectName, and a list of timeEntries),
 *  - allocations: an array of Allocation objects (each one ties a projectId to some number of allocated hours),
 * this function returns a new array where each unique projectId appears exactly once (only if registeredHours < allocatedHours),
 * and each entry has:
 *    { projectId, projectName, allocatedHours, registeredHours, missingHours }.
 *
 * It also adds any projectIds that appear in allocations but never appeared in projects:
 * those get registeredHours = 0 and missingHours = allocatedHours.
 */
export function calculateMissingAllocatedList(projects: UserRegistrationPeriod[], allocations: Allocation[]): MissingAllocatedEntry[] {
    // 1) Build a map of total allocated hours per projectId.
    //    { [projectId]: totalAllocatedHours }
    const allocationByProject: Record<string, number> = {};
    for (const alloc of allocations) {
        allocationByProject[alloc.projectId] = (allocationByProject[alloc.projectId] || 0) + alloc.hours;
    }

    // 2) Build a map of total registered hours per projectId,
    //    and preserve the projectName from the first registration period we see.
    //    { [projectId]: { projectName, registeredHours } }
    const registeredByProject: Record<string, { projectName: string; registeredHours: number }> = {};
    for (const project of projects) {
        const pid = project.projectId;
        const sumThisPeriod = project.timeEntries.reduce((sum, te) => sum + te.hours, 0);

        if (!registeredByProject[pid]) {
            registeredByProject[pid] = {
                projectName: project.projectName,
                registeredHours: sumThisPeriod,
            };
        } else {
            registeredByProject[pid].registeredHours += sumThisPeriod;
            // Keep the original projectName from the first occurrence
        }
    }

    // 3) For every projectId that has registrations, check if there's a deficit.
    const missingEntries: MissingAllocatedEntry[] = [];
    for (const pid of Object.keys(registeredByProject)) {
        const { projectName, registeredHours } = registeredByProject[pid];
        const allocatedHours = allocationByProject[pid] || 0;

        // Only include if registered < allocated
        if (registeredHours < allocatedHours) {
            missingEntries.push({
                projectId: pid,
                projectName,
                allocatedHours,
                registeredHours,
                missingHours: allocatedHours - registeredHours,
            });
        }
    }

    // 4) For each projectId that has allocations but no registrations, add it with registeredHours = 0.
    for (const [pid, totalAllocated] of Object.entries(allocationByProject)) {
        if (totalAllocated <= 0) {
            continue;
        }
        // Skip if we've already handled this pid above (i.e. it had registrations).
        if (registeredByProject[pid]) {
            continue;
        }

        // Find the projectName from allocations if possible, else fallback
        const firstAlloc = allocations.find(a => a.projectId === pid);
        const projectName = firstAlloc?.project?.name ?? '(Unknown project)';

        missingEntries.push({
            projectId: pid,
            projectName,
            allocatedHours: totalAllocated,
            registeredHours: 0,
            missingHours: totalAllocated,
        });
    }

    return missingEntries;
}
